import { Grid, TextField } from '@material-ui/core';
import { Autocomplete, FilterOptionsState } from '@material-ui/lab';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchIcon from '@material-ui/icons/Search';
import { searchUsersAndGroupsFromGraph, searchUsersFromGraph } from './GraphAutocompleteService';
import React, { useEffect } from 'react';
import { GraphGroupUser } from 'redux/models/graphGroupUser';
import { useDebouncedEffect } from 'helpers/reactHelper';
import './GraphAutocomplete.scss';

interface GraphAutocompleteProps {
	currentUserId: string;
	searchUsersOnly?: boolean;
	tagLimit?: number;
	fieldError?: boolean;
	onSelectUser: (users: any[]) => void;
}

const GraphAutocomplete: React.FC<GraphAutocompleteProps> = (props: GraphAutocompleteProps) => {
	const [options, setOptions]: any[] = React.useState([]);
	const [searchString, setSearchString] = React.useState('');
	const [searching, setSearching] = React.useState(false);
	const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);
	const loading = autoCompleteOpen && searching;
	const { currentUserId, searchUsersOnly = false, tagLimit, fieldError = false, onSelectUser } = props;

	const selectUser = (event: any, users: any) => {
		onSelectUser(users);
	};

	const handleOpen = () => {
		if (searchString.length > 0) {
			setSearching(true);
			setAutoCompleteOpen(true);
		} else {
			setAutoCompleteOpen(false);
		}
	};

	const handleInputChange = (event: any, newInputValue: string) => {
		setSearchString(newInputValue);
		if (newInputValue.length === 0) {
			setAutoCompleteOpen(false);
		} else {
			setSearching(true);
		}
	};

	// Perform Search
	useDebouncedEffect(
		() => {
			if (searching) {
				if (searchString.length === 0) return undefined;

				(async () => {
					let users: GraphGroupUser[] = [];
					if (searchUsersOnly) {
						users = await searchUsersFromGraph(searchString);
					} else {
						users = await searchUsersAndGroupsFromGraph(searchString);
					}

					if (users) {
						let usersWithCurrentUserRemoved = users.filter(user => user.id !== currentUserId);
						setOptions(usersWithCurrentUserRemoved);
						setSearching(false);
					}
				})();
			}
		},
		400,
		[loading, searching, searchString, currentUserId]
	);

	// clear search option if autocomplete drop-down closes
	useEffect(() => {
		if (!autoCompleteOpen) {
			setOptions([]);
			setSearching(false);
		}
	}, [autoCompleteOpen]);

	// Custom filter that allows the user/group search to search the entire object instead of just
	// the text displayed in the pill of a selected object. This enables us to search things like the
	// email address and by "[lastname] , [firstname]"
	const filterOptions = (options: GraphGroupUser[], state: FilterOptionsState<GraphGroupUser>) => {
		const filteredOptions = options.filter(option => {
			const optionValues = Object.entries(option).map(([key, value]) => {
				return key === 'id' || key === 'type' ? null : value && value.toString();
			});
			const addToList = optionValues.some((value: string) => {
				const valueIncludes = value && value.toLowerCase().includes(state.inputValue.toLowerCase());
				return valueIncludes;
			});

			return addToList;
		});

		return filteredOptions;
	};

	return (
		<>
			<Autocomplete
				id='user-search'
				open={autoCompleteOpen}
				onOpen={handleOpen}
				onClose={() => {
					setAutoCompleteOpen(false);
				}}
				multiple
				limitTags={tagLimit ? tagLimit : 1}
				inputValue={searchString}
				onInputChange={handleInputChange}
				options={options}
				loading={loading}
				onChange={selectUser}
				filterOptions={filterOptions}
				getOptionSelected={(option: GraphGroupUser, value: GraphGroupUser) => option.id === value.id}
				getOptionLabel={(option: GraphGroupUser) => option.name}
				renderInput={params => (
					<TextField
						{...params}
						fullWidth
						variant='outlined'
						label={`${searchUsersOnly ? 'Person lookup' : 'Person and group lookup'}`}
						placeholder='type to search'
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<React.Fragment>
									{loading ? <CircularProgress color='inherit' size={20} /> : null}
									<SearchIcon className='user-search__icon' />
								</React.Fragment>
							),
						}}
						error={fieldError}
					/>
				)}
				renderOption={option => (
					<Grid container direction='column' justify='center' alignItems='flex-start'>
						<div className='autocomplete-option__name'>{option.name}</div>
						{option.type === 'user' && <div className='autocomplete-option__email'>{option.email}</div>}
					</Grid>
				)}
				className='user-search-input'
			/>
			{/* TODO: When we're ready to add the abliity to upload a CSV we can uncomment the icon below, uncomment the inport of the icon at the top of this page,
								 and edit the create-team-input class to have a width of 90% */}
			{/* <MoreVertIcon /> */}
			<div className='user-search-input-subtext'>{`Search for people ${!searchUsersOnly ? 'or groups' : ''}`}</div>
		</>
	);
};
export default GraphAutocomplete;
